// help/AssetHelp.js

export default function AssetHelp() {
  return (
    <div className="space-y-8">

      {/* =======================================================
          HEADER
      ======================================================== */}

      <div>
        <h2 className="text-2xl font-bold">
          Assets
        </h2>

        <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400">
          The Assets section lets you manage your digital assets —
          GHSX, USWD, and USDT. You can view balances, receive
          supported assets, send funds, buy GHSX or USWD, and review
          your transaction history.
        </p>
      </div>

      {/* =======================================================
          RECEIVE
      ======================================================== */}

      <section>
        <h3 className="text-lg font-semibold">
          Receiving Assets
        </h3>

        <ul className="mt-3 list-disc pl-6 space-y-2 text-sm">
          <li>
            Open the <strong>Assets</strong> page and find the asset
            you want to receive — GHSX, USWD, or USDT.
          </li>

          <li>
            Copy the wallet address shown under that asset.
          </li>

          <li>
            Share the address with the sender, or use it on another
            wallet or exchange.
          </li>

          <li>
            Always send assets using the correct blockchain network —
            USDT is only supported on the Solana network, as SPL USDT.
          </li>

          <li>
            Received funds appear in your balance once the transfer is
            processed.
          </li>
        </ul>
      </section>

      {/* =======================================================
          SEND — GHSX
      ======================================================== */}

      <section>
        <h3 className="text-lg font-semibold">
          Sending GHSX
        </h3>

        <ul className="mt-3 list-disc pl-6 space-y-2 text-sm">
          <li>
            Press <strong>Send</strong>, choose <strong>Send to
            Metisel users</strong>, then select <strong>GHSX</strong>.
          </li>

          <li>
            Enter the recipient's GHSX address.
          </li>

          <li>
            Enter the amount to send, or tap <strong>MAX</strong> to
            send your full available balance minus the network fee.
          </li>

          <li>
            Review the amount, network fee, total deducted, and
            balance afterward on the confirmation screen before
            sending.
          </li>

          <li>
            Confirm to send. A network fee of 0.5 GHSX applies to
            every transfer.
          </li>
        </ul>
      </section>

      {/* =======================================================
          SEND — USWD
      ======================================================== */}

      <section>
        <h3 className="text-lg font-semibold">
          Sending USWD
        </h3>

        <ul className="mt-3 list-disc pl-6 space-y-2 text-sm">
          <li>
            Press <strong>Send</strong>, choose <strong>Send to
            Metisel users</strong>, then select <strong>USWD</strong>.
          </li>

          <li>
            Enter the recipient's USWD address.
          </li>

          <li>
            Enter the amount to send, or tap <strong>MAX</strong> to
            send your full available balance minus the network fee.
          </li>

          <li>
            Review the transfer details — amount, network fee, total
            deducted, and your balance afterward — before confirming.
            This action cannot be undone, so double-check the
            recipient address.
          </li>

          <li>
            Confirm to send. A network fee of 0.01 USWD applies to
            every transfer.
          </li>
        </ul>
      </section>

      {/* =======================================================
          SEND — USDT TO A METISEL USER
      ======================================================== */}

      <section>
        <h3 className="text-lg font-semibold">
          Sending USDT to a Metisel User
        </h3>

        <ul className="mt-3 list-disc pl-6 space-y-2 text-sm">
          <li>
            Press <strong>Send</strong>, choose <strong>Send to
            Metisel users</strong>, then select <strong>USDT</strong>.
          </li>

          <li>
            Enter the recipient's account email. Recently used emails
            are suggested for convenience.
          </li>

          <li>
            Enter the amount to send, or tap <strong>MAX</strong> to
            fill in your full available balance.
          </li>

          <li>
            Confirm to send.
          </li>
        </ul>

        <p className="mt-3 text-neutral-600 dark:text-neutral-400">
          Transfers between Metisel users are instant and free — no
          network fee applies.
        </p>
      </section>

      {/* =======================================================
          SEND — USDT ON-CHAIN
      ======================================================== */}

      <section>
        <h3 className="text-lg font-semibold">
          Sending USDT On-Chain
        </h3>

        <ul className="mt-3 list-disc pl-6 space-y-2 text-sm">
          <li>
            Press <strong>Send</strong>, choose <strong>On-chain
            withdraw</strong>, then select <strong>USDT</strong>.
          </li>

          <li>
            Enter the recipient's wallet address. USDT withdrawals
            must use a Solana address.
          </li>

          <li>
            Enter the amount to send, or tap <strong>MAX</strong> —
            this automatically leaves room for the network fee.
          </li>

          <li>
            The minimum amount you can send on-chain is 0.1 USDT, and
            a network fee of 0.01 USDT applies to every transfer.
          </li>

          <li>
            Confirm to send. You'll see a success screen with the
            transaction details, including the transaction signature,
            once it completes.
          </li>
        </ul>
      </section>

      {/* =======================================================
          BUY — GHSX
      ======================================================== */}

      <section>
        <h3 className="text-lg font-semibold">
          Buying GHSX
        </h3>

        <ul className="mt-3 list-disc pl-6 space-y-2 text-sm">
          <li>
            From the GHSX card on the Assets page, tap
            <strong> Buy GHSX</strong>. This option is available to
            approved sellers.
          </li>

          <li>
            Choose USDT as your payment method — this is currently the
            only supported payment method.
          </li>

          <li>
            Enter the amount of GHSX you want to buy, or tap
            <strong> MAX</strong> to buy as much as your available
            USDT balance covers.
          </li>

          <li>
            Review the rate, total USDT cost, and your USDT balance
            afterward before confirming. This action cannot be undone.
          </li>

          <li>
            Confirm to complete the purchase. GHSX is priced at a
            fixed rate of 0.05 USDT per GHSX, and your GHSX and USDT
            balances update automatically.
          </li>
        </ul>
      </section>

      {/* =======================================================
          BUY — USWD
      ======================================================== */}

      <section>
        <h3 className="text-lg font-semibold">
          Buying USWD
        </h3>

        <ul className="mt-3 list-disc pl-6 space-y-2 text-sm">
          <li>
            From the USWD card on the Assets page, tap
            <strong> Buy USWD</strong>. This option is available to
            approved sellers.
          </li>

          <li>
            Choose USDT as your payment method — this is currently the
            only supported payment method.
          </li>

          <li>
            Enter the amount of USWD you want to buy, or tap
            <strong> MAX</strong> to buy as much as your available
            USDT balance covers.
          </li>

          <li>
            Review the rate, total USDT cost, and your USDT balance
            afterward before confirming. This action cannot be undone.
          </li>

          <li>
            Confirm to complete the purchase. USWD is priced at a
            fixed rate of 0.5 USDT per USWD, and your USWD and USDT
            balances update automatically.
          </li>
        </ul>
      </section>

      {/* =======================================================
          HISTORY
      ======================================================== */}

      <section>
        <h3 className="text-lg font-semibold">
          Transaction History
        </h3>

        <ul className="mt-3 list-disc pl-6 space-y-2 text-sm">
          <li>
            Open <strong>Transactions</strong> from the Assets page.
          </li>

          <li>
            View all incoming and outgoing transfers in
            chronological order, marked as Received or Sent.
          </li>

          <li>
            Each transaction shows the amount and date and time.
          </li>

          <li>
            On-chain transactions include a
            <strong> View on Solscan</strong> link so you can verify
            them on the blockchain.
          </li>

          <li>
            Transactions with funds still reserved show a
            <strong> Committed</strong> amount alongside the total.
          </li>

          <li>
            Use your transaction history to verify completed transfers
            and monitor wallet activity.
          </li>
        </ul>
      </section>

      {/* =======================================================
          BALANCES
      ======================================================== */}

      <section>
        <h3 className="text-lg font-semibold">
          Understanding Balances
        </h3>

        <ul className="mt-3 list-disc pl-6 space-y-2 text-sm">
          <li>
            <strong>Total Balance</strong> is the total amount of the
            asset in your wallet.
          </li>

          <li>
            <strong>Committed Balance</strong> represents funds that
            are temporarily reserved for active transactions and cannot
            be spent until they are released.
          </li>

          <li>
            <strong>Available Balance</strong> is the amount you can
            immediately send, buy with, or withdraw.
          </li>

          <li>
            Buying GHSX or USWD automatically updates the relevant
            asset balance and your USDT balance.
          </li>

          <li>
            For USWD, a <strong>Commission</strong> balance and a
            <strong> Sell rate</strong> are shown when they apply to
            your account.
          </li>

          <li>
            Always ensure your available balance covers both the
            transfer amount and any applicable network fee — the
            <strong> MAX</strong> button accounts for this
            automatically.
          </li>
        </ul>
      </section>

    </div>
  );
}